import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete, Box, Paper, TextField, Typography } from '@mui/material';

import { SearchOutlined } from '@mui/icons-material';

const PatientAutocomplete = ({ onSelectPatient, patients,  width = '100%', placeholder
 }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

 const handleChange = (event, patient) => {
  setSelectedPatient(patient);

  if (patient) {
    onSelectPatient(patient);
    setInputValue(patient.name); // or `${patient.name} - ${patient.mr}`
  }

  setOpen(false);
};
const handleInputChange = (event, newInputValue, reason) => {
  setInputValue(newInputValue);

  if (reason === 'clear') {
    setSelectedPatient(null);
    onSelectPatient(null);
    setOpen(false);
    return;
  }

  if (reason === 'input') {
    setOpen(true);
  }
};
  const filterPatients = (options, state) => {
    const searchValue = state.inputValue.trim().toLowerCase();

    if (!searchValue) {
      return options;
    }

    return options.filter(patient => {
      const patientDetails = [
        patient.name,
        patient.mr,
        patient.dob,
        patient.sex
      ]
        .join(' ')
        .toLowerCase();

      return patientDetails.includes(searchValue);
    });
  };

  const PatientPaper = paperProps => {
    const { children, ...rest } = paperProps;

    return (
      <Paper
        {...rest}
        elevation={4}
        sx={{
          border: '1px solid #E0E0E0',
          borderRadius: '4px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
          mt: 0.5,
          overflow: 'hidden'
        }}>
        {children}

        <Box
          sx={{
            bgcolor: '#FAFAFA',
            borderTop: '1px solid #E5E5E5',
            px: 2.5,
            py: 1.5
          }}>
          <Typography
            sx={{
              color: '#424242',
              fontSize: 13
            }}>
            Organisation has {patients.length} patients
          </Typography>
        </Box>
      </Paper>
    );
  };

  PatientPaper.propTypes = {
    children: PropTypes.node
  };

  return (
    <Autocomplete
      autoHighlight
      value={selectedPatient}
      sx={{ width }}
      clearOnBlur={false}
      filterOptions={filterPatients}
      getOptionLabel={option => option?.name || ''}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.mr === value.mr}
      noOptionsText="No patients found"
      onChange={handleChange}
      onClose={(event, reason) => {
        // Close on outside click, Escape, or selection
        if (
          reason === 'blur' ||
          reason === 'escape' ||
          reason === 'selectOption'
        ) {
          setOpen(false);
        }
      }}
      onInputChange={handleInputChange}
      onOpen={() => setOpen(true)}
      open={open}
      openOnFocus
      options={patients}
      renderInput={params => (
        <Box
          sx={{
            position: 'relative',
            width: '100%'
          }}>
          <SearchOutlined
            sx={{
              color: '#9E9E9E',
              fontSize: 18,
              left: 6,
              pointerEvents: 'none',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2
            }}
          />

          <TextField
            {...params}
            onClick={() => setOpen(true)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            size="small"
            sx={{

              '& .MuiOutlinedInput-root': {
                bgcolor: '#FFFFFF',
                borderRadius: '4px',
                fontSize: 13,
                height: 38,
                minHeight: 38,
                paddingLeft: '24px !important',
                paddingRight: '8px !important'
              },

              '& .MuiAutocomplete-input': {
                padding: '2px 4px !important'
              },

              '& .MuiAutocomplete-endAdornment': {
                display: 'none'
              },

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#BDBDBD'
              },

              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#BDBDBD'
              },

              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#BDBDBD',
                borderWidth: 1
              },

              '& input::placeholder': {
                color: '#757575',
                opacity: 1
              }
            }}
          />
        </Box>
      )}
      renderOption={(props, patient) => {
        const { key, ...optionProps } = props;

        return (
          <Box
            {...optionProps}
            component="li"
            key={key}
            sx={{
              alignItems: 'flex-start !important',
              bgcolor: '#FFFFFF',
              borderBottom: '1px solid #E5E5E5',
              display: 'flex',
              flexDirection: 'column',
              px: '20px !important',
              py: '9px !important',

              '&:hover': {
                bgcolor: '#FBF7F1 !important'
              },

              '&.Mui-focused': {
                bgcolor: '#FBF7F1 !important'
              },

              '&[aria-selected="true"]': {
                bgcolor: '#FBF7F1 !important'
              }
            }}>
            <Typography
              sx={{
                color: '#424242',
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.4
              }}>
              {patient.name}
            </Typography>

            <Typography
              sx={{
                color: '#424242',
                fontSize: 13,
                lineHeight: 1.5,
                mt: 0.25
              }}>
              MR# {patient.mr} &nbsp;•&nbsp; {patient.dob}
              &nbsp;•&nbsp; {patient.sex}
            </Typography>
          </Box>
        );
      }}
      slots={{
        paper: PatientPaper
      }}
      slotProps={{
        listbox: {
          sx: {
            maxHeight: 320,
            p: 0
          }
        },

        popper: {
          placement: 'bottom-start',
          sx: {
            minWidth: '300px !important',
            zIndex: 1500
          }
        }
      }}
    />
  );
};

PatientAutocomplete.propTypes = {
  onSelectPatient: PropTypes.func.isRequired,
  patients: PropTypes.arrayOf(
    PropTypes.shape({
      dob: PropTypes.string.isRequired,
      mr: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      sex: PropTypes.string.isRequired
    })
  ).isRequired,
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])
};

export default PatientAutocomplete;
